import React, { useMemo, useState } from "react";
import { getCenikItem } from "../../data/cenikUtils";

const pageStyle = {
  marginTop: 18,
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 20,
  padding: 20,
  boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
};

const topGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
  marginTop: 16,
};

const statCardStyle = {
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 16,
  padding: 14,
};

const toolbarStyle = {
  display: "grid",
  gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr auto auto",
  gap: 10,
  marginTop: 18,
  alignItems: "center",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid #d1d5db",
  outline: "none",
  background: "#fff",
};

const actionBtnStyle = {
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid #d1d5db",
  background: "#fff",
  color: "#111827",
  cursor: "pointer",
  fontWeight: 700,
};

const splitStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(360px, 520px) minmax(0, 1fr)",
  gap: 18,
  marginTop: 18,
};

const listWrapStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 18,
  overflow: "hidden",
  background: "#fff",
};

const detailWrapStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 18,
  background: "#fff",
  padding: 16,
  minHeight: 600,
};

const rowCardStyle = {
  padding: 14,
  borderBottom: "1px solid #eef2f7",
  cursor: "pointer",
};

const badgeBase = {
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 8px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 800,
  border: "1px solid transparent",
};

function valueOrDash(value) {
  const v = String(value ?? "").trim();
  return v || "—";
}

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(value) {
  const n = toNumber(value);
  return `${n.toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kč`;
}

function formatDate(value) {
  const v = String(value || "").trim();
  if (!v) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split("-");
    return `${d}.${m}.${y}`;
  }
  return v;
}

function normalizeTypeLabel(type) {
  const t = String(type || "").trim();
  if (t === "kremace") return "Kremace";
  if (t === "kremace_bez_obradu") return "Kremace bez obřadu";
  if (t === "pohreb") return "Pohřeb";
  if (t === "obrad_s_urnou") return "Obřad s urnou";
  if (t === "alternativa") return "Alternativa";
  if (t === "prevoz") return "Převoz";
  if (t === "ostatni") return "Ostatní";
  return t || "Neurčeno";
}

function computeFinance(row) {
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  const sluzby = Array.isArray(row?.sluzby) ? row.sluzby : [];
  const kvetiny = row?.kvetiny || {};
  const platba = row?.platba || {};

  let servicesTotal = 0;
  for (const item of sluzby) {
    if (!item) continue;

    const cenik = getCenikItem(item?.id);
    if (!cenik) continue;

    const rakevItem = item?.rakevId ? getCenikItem(item.rakevId) : null;
    const cenaAktualni =
      item?.id === "rakev" && rakevItem
        ? Number(rakevItem?.cenaBezDph) || 0
        : item?.cenaBezDphOverride !== "" && item?.cenaBezDphOverride !== undefined
          ? Number(item?.cenaBezDphOverride) || 0
          : Number(cenik?.cenaBezDph) || 0;

    const qty = Number(item?.mnozstvi) || 0;
    const bez = round2(cenaAktualni * qty);
    const dph = round2((bez * (Number(cenik?.dph) || 0)) / 100);
    const celkem = round2(bez + dph);

    servicesTotal += celkem;
  }

  const normalizeKvetina = (item) => ({
    objednano: !!item?.objednano,
    dodavatel: item?.dodavatel || "",
    typVazby: item?.typVazby || "",
    barva: item?.barva || "",
    stuhaBarva: item?.stuhaBarva || "",
    stuhaText1: item?.stuhaText1 || "",
    stuhaText2: item?.stuhaText2 || "",
    poznamka: item?.poznamka || "",
    mnozstvi: Number(item?.mnozstvi || 1) || 1,
    cenaBezDph: Number(item?.cenaBezDph || 0) || 0,
    cenaSDph: Number(item?.cenaSDph || 0) || 0,
    dph: Number(item?.dph || 0) || 0,
  });

  const isFilledKvetina = (item) =>
    !!(
      item?.objednano ||
      String(item?.dodavatel || "").trim() ||
      String(item?.typVazby || "").trim() ||
      String(item?.barva || "").trim() ||
      String(item?.stuhaBarva || "").trim() ||
      String(item?.stuhaText1 || "").trim() ||
      String(item?.stuhaText2 || "").trim() ||
      String(item?.poznamka || "").trim() ||
      (Number(item?.cenaSDph || 0) || 0) > 0 ||
      (Number(item?.cenaBezDph || 0) || 0) > 0
    );

  const rawKvetinyItems = Array.isArray(kvetiny?.items) ? kvetiny.items : [];
  const kvetinyItems = rawKvetinyItems.length > 0
    ? rawKvetinyItems.map(normalizeKvetina).filter(isFilledKvetina)
    : (() => {
        const legacy = normalizeKvetina(kvetiny || {});
        return isFilledKvetina(legacy) ? [legacy] : [];
      })();

  const flowersTotal = kvetinyItems.reduce((acc, item) => {
    const mnozstvi = Number(item?.mnozstvi || 1) || 1;
    const bez = round2((Number(item?.cenaBezDph || 0) || 0) * mnozstvi);
    const dphCastka = round2((bez * (Number(item?.dph || 0) || 0)) / 100);
    const celkem = round2(bez + dphCastka);
    return acc + celkem;
  }, 0);

  const rawZaloha =
    row?.finance?.zalohaCastka ??
    platba?.zalohovaPlatba ??
    platba?.zalohaCastka ??
    row?.zalohaCastka ??
    0;

  const zalohy = round2(Number(rawZaloha) || 0);

  const rawUhrazeno =
    row?.finance?.uhrazenoCelkem ??
    platba?.uhrazenoCelkem ??
    row?.uhrazenoCelkem;

  const uhrazeno = round2(
    rawUhrazeno === "" || rawUhrazeno === null || rawUhrazeno === undefined
      ? zalohy
      : (Number(rawUhrazeno) || 0)
  );

  const total = round2(servicesTotal + flowersTotal);
  const outstanding = round2(Math.max(0, total - uhrazeno));

  let state = "neuhrazeno";
  if (total <= 0 && uhrazeno <= 0) state = "bez_castek";
  else if (outstanding <= 0 && total > 0) state = "uhrazeno";
  else if (uhrazeno > 0) state = "castecne";

  return {
    servicesTotal: round2(servicesTotal),
    flowersTotal: round2(flowersTotal),
    total,
    zalohy,
    uhrazeno,
    outstanding,
    state,
  };
}

function financeBadge(state) {
  if (state === "uhrazeno") {
    return {
      ...badgeBase,
      background: "#f0fdf4",
      color: "#166534",
      borderColor: "#bbf7d0",
    };
  }
  if (state === "castecne") {
    return {
      ...badgeBase,
      background: "#eff6ff",
      color: "#1d4ed8",
      borderColor: "#bfdbfe",
    };
  }
  if (state === "zalohovano") {
    return {
      ...badgeBase,
      background: "#fff7ed",
      color: "#c2410c",
      borderColor: "#fed7aa",
    };
  }
  if (state === "bez_castek") {
    return {
      ...badgeBase,
      background: "#f8fafc",
      color: "#475569",
      borderColor: "#cbd5e1",
    };
  }
  return {
    ...badgeBase,
    background: "#fef2f2",
    color: "#991b1b",
    borderColor: "#fecaca",
  };
}

function financeLabel(state) {
  if (state === "uhrazeno") return "Uhrazeno";
  if (state === "castecne") return "Částečně uhrazeno";
  if (state === "zalohovano") return "Jen záloha";
  if (state === "bez_castek") return "Bez částek";
  return "Neuhrazeno";
}

function normalizeInvoiceDateOnly(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (m) {
    const dd = m[1].padStart(2, "0");
    const mm = m[2].padStart(2, "0");
    const yyyy = m[3];
    return `${yyyy}-${mm}-${dd}`;
  }
  return "";
}

function computeVyjezdInvoiceStatus(row) {
  const prevoz = row?.prevoz || {};
  const vyjezd = row?.vyjezd || row?.raw?.vyjezd || {};
  const zaklad = row?.zaklad || {};

  const aktivniPcrVetev =
    typeof prevoz?.fakturaceRozpad?.pcr?.aktivni === "boolean"
      ? prevoz.fakturaceRozpad.pcr.aktivni
      : String(zaklad?.vyjezdPlatce || vyjezd?.vyjezdPlatce || "").trim().toLowerCase() === "pcr";

  const aktivniPojistovnaVetev =
    typeof prevoz?.fakturaceRozpad?.pojistovna?.aktivni === "boolean"
      ? prevoz.fakturaceRozpad.pojistovna.aktivni
      : String(zaklad?.vyjezdPlatce || vyjezd?.vyjezdPlatce || "").trim().toLowerCase() == "pojistovna";

  const aktivniRodinnaVetev =
    typeof prevoz?.fakturaceRozpad?.rodina?.aktivni === "boolean"
      ? prevoz.fakturaceRozpad.rodina.aktivni
      : !!prevoz?.navazujiciRodinnyPrevoz;

  const faktura =
    aktivniPcrVetev
      ? (prevoz?.externiFakturace?.pcr || vyjezd?.externiFakturace?.pcr || {})
      : aktivniPojistovnaVetev
        ? (prevoz?.externiFakturace?.pojistovna || vyjezd?.externiFakturace?.pojistovna || {})
        : aktivniRodinnaVetev
          ? (prevoz?.rodinnaFakturace || {})
          : {};

  const rawStav = String(faktura?.stav || "").trim().toLowerCase();
  const datumSplatnosti = normalizeInvoiceDateOnly(faktura?.datumSplatnosti || faktura?.splatnost);
  const odeslanoDne = String(faktura?.odeslanoDne || "").trim();
  const today = new Date().toISOString().slice(0, 10);

  if (["uhrazeno", "zaplaceno", "proplaceno", "uhrazena", "uhrazená", "uhrazeno_cele"].includes(rawStav)) {
    return { id: "uhrazeno", label: "Uhrazeno", tone: "green" };
  }

  if (datumSplatnosti && datumSplatnosti < today) {
    return { id: "po_splatnosti", label: "Po splatnosti", tone: "red" };
  }

  if (odeslanoDne || ["odeslano", "vystaveno", "cekame_na_uhradu", "čekáme_na_úhradu"].includes(rawStav)) {
    return { id: "odeslano", label: "Odesláno", tone: "blue" };
  }

  return { id: "neodeslano", label: "Neodesláno", tone: "slate" };
}

function vyjezdInvoiceBadge(status) {
  const tone = String(status?.tone || "slate");

  if (tone === "green") return { ...badgeBase, background: "#dcfce7", color: "#166534", borderColor: "#86efac" };
  if (tone === "red") return { ...badgeBase, background: "#fee2e2", color: "#991b1b", borderColor: "#fca5a5" };
  if (tone === "blue") return { ...badgeBase, background: "#dbeafe", color: "#1d4ed8", borderColor: "#93c5fd" };
  return { ...badgeBase, background: "#f3f4f6", color: "#4b5563", borderColor: "#d1d5db" };
}

function computeFamilyStatus(row) {
  const navazuje = !!row?.prevoz?.navazujiciRodinnyPrevoz;
  if (navazuje) {
    return { id: "navazuje_rodina", label: "Navazuje rodinná část", tone: "green" };
  }
  return { id: "jen_vyjezd", label: "Jen výjezd", tone: "slate" };
}

function familyStatusBadge(status) {
  const tone = String(status?.tone || "slate");
  if (tone === "green") {
    return {
      ...badgeBase,
      background: "#f0fdf4",
      color: "#166534",
      borderColor: "#bbf7d0",
    };
  }
  return {
    ...badgeBase,
    background: "#f8fafc",
    color: "#475569",
    borderColor: "#cbd5e1",
  };
}

function computeOrderStatus(row) {
  const rawState = String(
    row?.zaklad?.stavZakazky ||
    row?.raw?.stavKalendar ||
    row?.raw?.stavZakazky ||
    ""
  ).trim().toLowerCase();

  const obradDatum = String(row?.obrad?.datum || "").trim();
  const obradCas = String(row?.obrad?.cas || "").trim();
  const prevozDatum = String(row?.prevoz?.datumPrevozu || "").trim();
  const prevozCas = String(row?.prevoz?.casPrevozu || "").trim();
  const financeState = String(row?.finance?.state || "").trim();

  let eventDateTime = null;

  if (obradDatum) {
    const dt = `${obradDatum}T${(obradCas || "00:00").slice(0, 5)}:00`;
    const parsed = new Date(dt);
    if (!Number.isNaN(parsed.getTime())) eventDateTime = parsed;
  } else if (prevozDatum) {
    const dt = `${prevozDatum}T${(prevozCas || "00:00").slice(0, 5)}:00`;
    const parsed = new Date(dt);
    if (!Number.isNaN(parsed.getTime())) eventDateTime = parsed;
  }

  const now = new Date();
  const eventPassed = !!eventDateTime && eventDateTime.getTime() <= now.getTime();

  if (rawState.includes("storno")) {
    return { id: "storno", label: "Storno", tone: "red" };
  }

  if (rawState.includes("hotovo") || rawState.includes("uzavreno") || rawState.includes("uzavřeno")) {
    return { id: "hotovo", label: "Hotovo", tone: "green" };
  }

  if (eventPassed) {
    if (financeState === "uhrazeno") {
      return { id: "hotovo", label: "Hotovo", tone: "green" };
    }
    return { id: "cek_fakturace", label: "Hotovo / čeká na fakturaci", tone: "amber" };
  }

  if (financeState === "castecne" || financeState === "zalohovano") {
    return { id: "doplatek", label: "Čeká na doplatek", tone: "amber" };
  }

  if (eventDateTime) {
    return { id: "cek_obrad", label: "Čeká na obřad", tone: "blue" };
  }

  return { id: "rozpracovano", label: "Rozpracováno", tone: "slate" };
}

function orderStatusBadge(status) {
  const tone = String(status?.tone || "slate");

  if (tone === "green") {
    return {
      ...badgeBase,
      background: "#f0fdf4",
      color: "#166534",
      borderColor: "#bbf7d0",
    };
  }

  if (tone === "blue") {
    return {
      ...badgeBase,
      background: "#eff6ff",
      color: "#1d4ed8",
      borderColor: "#bfdbfe",
    };
  }

  if (tone === "amber") {
    return {
      ...badgeBase,
      background: "#fffbeb",
      color: "#92400e",
      borderColor: "#fde68a",
    };
  }

  if (tone === "red") {
    return {
      ...badgeBase,
      background: "#fef2f2",
      color: "#991b1b",
      borderColor: "#fecaca",
    };
  }

  return {
    ...badgeBase,
    background: "#f8fafc",
    color: "#475569",
    borderColor: "#cbd5e1",
  };
}

function detailBlock(title, content) {
  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 16,
        padding: 14,
        background: "#fff",
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 900, color: "#111827", marginBottom: 10 }}>
        {title}
      </div>
      {content}
    </div>
  );
}

function infoGrid(items) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 10,
      }}
    >
      {items.map((item, idx) => (
        <div
          key={`${item.label}-${idx}`}
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            padding: "10px 12px",
          }}
        >
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>{item.label}</div>
          <div style={{ fontSize: 14, color: "#111827", fontWeight: 800, marginTop: 4 }}>
            {valueOrDash(item.value)}
          </div>
        </div>
      ))}
    </div>
  );
}

function normalizeRow(item) {
  const src =
    item?.formData ||
    item?.data ||
    item?.order ||
    item?.item ||
    item ||
    {};

  const zaklad = src?.zaklad || {};
  const vstupni = src?.vstupniInformace || {};
  const zesnula = src?.zesnulaOsoba || {};
  const vypravitel = src?.vypravitel || {};
  const obrad = src?.obrad || {};
  const prevoz = src?.prevoz || {};
  const platba = src?.platba || {};
  const rec = src?.rec || {};

  const id = String(
    item?.id ||
    item?.orderId ||
    item?.zakazkaId ||
    src?.id ||
    src?.orderId ||
    src?.zakazkaId ||
    zaklad?.cisloZakazky ||
    ""
  ).trim();

  const typ = String(zaklad?.typZakazky || "").trim();
  const typLabel = normalizeTypeLabel(typ);

  const zesnuly = String(
    zaklad?.zesnuly ||
    [vstupni?.jmenoZesnuleho, vstupni?.prijmeniZesnuleho].filter(Boolean).join(" ") ||
    [zesnula?.jmeno, zesnula?.prijmeni].filter(Boolean).join(" ") ||
    ""
  ).trim();

  const objednavatel = String(
    zaklad?.vypravitel ||
    [vypravitel?.jmeno, vypravitel?.prijmeni].filter(Boolean).join(" ") ||
    ""
  ).trim();

  const datumObradu = String(obrad?.datum || "").trim();
  const casObradu = String(obrad?.cas || "").trim();
  const datumPrevozu = String(prevoz?.datumPrevozu || "").trim();
  const casPrevozu = String(prevoz?.casPrevozu || "").trim();

  const hlavniDatum = datumObradu || datumPrevozu || String(zaklad?.datumSjednani || "").trim();
  const hlavniCas = casObradu || casPrevozu;

  const misto = String(
    obrad?.misto ||
    prevoz?.mistoOdvozu ||
    prevoz?.adresaOdvozu ||
    ""
  ).trim();

  const note = String(
    obrad?.poznamka ||
    prevoz?.poznamka ||
    zaklad?.poznamka ||
    vstupni?.poznamka ||
    ""
  ).trim();

  const finance = computeFinance(src);

    const orderStatus = computeOrderStatus({
      id,
      raw: src,
      zaklad,
      obrad,
      prevoz,
      finance,
      hlavniDatum,
    });
    const familyStatus = computeFamilyStatus({
      id,
      raw: src,
      zaklad,
      vstupni,
      zesnula,
      vypravitel,
      obrad,
      prevoz,
      platba,
      rec,
      typ,
      typLabel,
      zesnuly,
      objednavatel,
      hlavniDatum,
      hlavniCas,
      misto,
      note,
      finance,
      orderStatus,
    });

    const vyjezdInvoiceStatus = computeVyjezdInvoiceStatus({
      id,
      raw: src,
      zaklad,
      prevoz,
    });

  const searchBlob = [
    id,
    typLabel,
    zesnuly,
    objednavatel,
    hlavniDatum,
    hlavniCas,
    misto,
    note,
    vypravitel?.telefon,
    vypravitel?.email,
  ].join(" ").toLowerCase();

  return {
    id,
    raw: src,
    zaklad,
    vstupni,
    zesnula,
    vypravitel,
    obrad,
    prevoz,
    platba,
    rec,
    typ,
    typLabel,
    zesnuly,
    objednavatel,
    hlavniDatum,
    hlavniCas,
    misto,
    note,
    finance,
      orderStatus,
      familyStatus,
      vyjezdInvoiceStatus,
    searchBlob,
  };
}

function downloadFile(filename, content, mime = "text/plain;charset=utf-8") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function buildCsv(rows) {
  const escape = (v) => {
    const s = String(v ?? "");
    return `"${s.replace(/"/g, '""')}"`;
  };

  const header = [
    "ID zakázky",
    "Typ",
    "Zesnulý",
    "Objednavatel",
    "Datum",
    "Čas",
    "Místo",
    "Finance stav",
    "Celkem",
    "Uhrazeno",
    "Zbývá",
    "Poznámka",
  ];

  const lines = rows.map((row) => [
    row.id,
    row.typLabel,
    row.zesnuly,
    row.objednavatel,
    formatDate(row.hlavniDatum),
    row.hlavniCas,
    row.misto,
    financeLabel(row.finance.state),
    row.finance.total,
    row.finance.uhrazeno,
    row.finance.outstanding,
    row.note,
  ]);

  return [header, ...lines].map((line) => line.map(escape).join(";")).join("\n");
}

export default function Pohrebka2BookPanel({ ordersList = [], order = null, setOrder = null, saveOrder = null, onOpenOrder }) {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [financeFilter, setFinanceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [familyFilter, setFamilyFilter] = useState("");
  const [vyjezdInvoiceFilter, setVyjezdInvoiceFilter] = useState("");
  const [sortBy, setSortBy] = useState("date_desc");
  const [selectedId, setSelectedId] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);

  const rows = useMemo(
    () => (Array.isArray(ordersList) ? ordersList : [])
      .map(normalizeRow)
      .filter((row) => row.id),
    [ordersList]
  );

  const filteredRows = useMemo(() => {
    let out = [...rows];
    const q = String(search || "").trim().toLowerCase();

    if (q) {
      out = out.filter((row) => row.searchBlob.includes(q));
    }

    if (typeFilter) {
      out = out.filter((row) => row.typ === typeFilter);
    }

    if (financeFilter) {
      out = out.filter((row) => row.finance.state === financeFilter);
    }

    if (statusFilter) {
      out = out.filter((row) => String(row.orderStatus?.id || "") === statusFilter);
    }

    if (familyFilter) {
      out = out.filter((row) => String(row.familyStatus?.id || "") === familyFilter);
    }

    if (vyjezdInvoiceFilter) {
      out = out.filter((row) => String(row.vyjezdInvoiceStatus?.id || "") === vyjezdInvoiceFilter);
    }

    out.sort((a, b) => {
      if (sortBy === "date_asc") {
        return String(a.hlavniDatum || "").localeCompare(String(b.hlavniDatum || ""));
      }
      if (sortBy === "name_asc") {
        return String(a.zesnuly || "").localeCompare(String(b.zesnuly || ""), "cs");
      }
      if (sortBy === "name_desc") {
        return String(b.zesnuly || "").localeCompare(String(a.zesnuly || ""), "cs");
      }
      if (sortBy === "finance_desc") {
        return b.finance.total - a.finance.total;
      }
      return String(b.hlavniDatum || "").localeCompare(String(a.hlavniDatum || ""));
    });

    return out;
  }, [rows, search, typeFilter, financeFilter, statusFilter, familyFilter, vyjezdInvoiceFilter, sortBy]);

  const selectedRow = useMemo(() => {
    if (!filteredRows.length) return null;
    return filteredRows.find((x) => x.id === selectedId) || filteredRows[0];
  }, [filteredRows, selectedId]);

  const stats = useMemo(() => {
    const total = filteredRows.length;
    const uhrazeno = filteredRows.filter((x) => x.finance.state === "uhrazeno").length;
    const neuhrazeno = filteredRows.filter((x) => x.finance.state === "neuhrazeno").length;
    const castecne = filteredRows.filter((x) => x.finance.state === "castecne").length;
    const kFakturaci = filteredRows.filter((x) => String(x.orderStatus?.id || "") === "cek_fakturace").length;
      const navazujeRodina = filteredRows.filter((x) => String(x.familyStatus?.id || "") === "navazuje_rodina").length;
      const jenVyjezd = filteredRows.filter((x) => String(x.familyStatus?.id || "") === "jen_vyjezd").length;
      const neodeslanoVyjezd = filteredRows.filter((x) => String(x.vyjezdInvoiceStatus?.id || "") === "neodeslano").length;
      const odeslanoVyjezd = filteredRows.filter((x) => String(x.vyjezdInvoiceStatus?.id || "") === "odeslano").length;
      const poSplatnostiVyjezd = filteredRows.filter((x) => String(x.vyjezdInvoiceStatus?.id || "") === "po_splatnosti").length;
      const uhrazenoVyjezd = filteredRows.filter((x) => String(x.vyjezdInvoiceStatus?.id || "") === "uhrazeno").length;
    const suma = filteredRows.reduce((sum, x) => sum + x.finance.total, 0);
    const uhrazenoCastka = filteredRows.reduce((sum, x) => sum + x.finance.uhrazeno, 0);
    const zbyva = filteredRows.reduce((sum, x) => sum + x.finance.outstanding, 0);

    return { total, uhrazeno, neuhrazeno, castecne, kFakturaci, navazujeRodina, jenVyjezd, suma, uhrazenoCastka, zbyva };
  }, [filteredRows]);

  const exportAllJson = () => {
    downloadFile(
      `kniha-zesnulych-${new Date().toISOString().slice(0, 10)}.json`,
      JSON.stringify(filteredRows.map((x) => x.raw), null, 2),
      "application/json;charset=utf-8"
    );
  };

  const exportAllCsv = () => {
    downloadFile(
      `kniha-zesnulych-${new Date().toISOString().slice(0, 10)}.csv`,
      buildCsv(filteredRows),
      "text/csv;charset=utf-8"
    );
  };

  const exportDetailJson = () => {
    if (!selectedRow) return;
    downloadFile(
      `zakazka-${selectedRow.id}.json`,
      JSON.stringify(selectedRow.raw, null, 2),
      "application/json;charset=utf-8"
    );
  };

  const saveManualStatus = async (nextStatus) => {
    if (!selectedRow?.id || typeof setOrder !== "function" || typeof saveOrder !== "function") return;

    setStatusSaving(true);
    try {
      await onOpenOrder?.(selectedRow.id, "zaklad");

      setOrder((prev) => ({
        ...(prev || {}),
        zaklad: {
          ...((prev && prev.zaklad) || {}),
          stavZakazky: String(nextStatus || ""),
        },
      }));

      await saveOrder();
      window.alert("Stav zakázky byl uložen.");
    } catch (e) {
      window.alert(`Nepodařilo se uložit stav zakázky: ${String(e?.message || e)}`);
    } finally {
      setStatusSaving(false);
    }
  };

  const printDetail = () => {
    if (!selectedRow) return;
    window.print();
  };

  return (
    <div style={pageStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 28, fontWeight: 900, color: "#111827" }}>Kniha zesnulých</div>
          <div style={{ marginTop: 6, color: "#6b7280", fontWeight: 600 }}>
            Přehled zakázek, financí a detailů pro rychlou práci i budoucí účetní napojení.
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button type="button" style={actionBtnStyle} onClick={exportAllCsv}>
            Export CSV
          </button>
          <button type="button" style={actionBtnStyle} onClick={exportAllJson}>
            Export JSON
          </button>
        </div>
      </div>

      <div style={topGridStyle}>
        <div style={statCardStyle}>
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>Zakázky</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: "#111827", marginTop: 6 }}>{stats.total}</div>
        </div>
        <div
          style={{ ...statCardStyle, cursor: "pointer" }}
          onClick={() => setStatusFilter("cek_fakturace")}
          title="Zobrazit jen zakázky čekající na fakturaci"
        >
          <div style={{ fontSize: 12, color: "#92400e", fontWeight: 800 }}>K fakturaci</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: "#92400e", marginTop: 6 }}>{stats.kFakturaci}</div>
        </div>
          <div
            style={{ ...statCardStyle, cursor: "pointer" }}
            onClick={() => setFamilyFilter("navazuje_rodina")}
            title="Zobrazit jen zakázky, kde navazuje rodinná část"
          >
            <div style={{ fontSize: 12, color: "#166534", fontWeight: 800 }}>Navazuje rodinná část</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: "#166534", marginTop: 6 }}>{stats.navazujeRodina}</div>
          </div>
          <div
            style={{ ...statCardStyle, cursor: "pointer" }}
            onClick={() => setFamilyFilter("jen_vyjezd")}
            title="Zobrazit jen výjezdové zakázky bez navazující rodinné části"
          >
            <div style={{ fontSize: 12, color: "#475569", fontWeight: 800 }}>Jen výjezd</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: "#475569", marginTop: 6 }}>{stats.jenVyjezd}</div>
          </div>

          <div
            style={{ ...statCardStyle, cursor: "pointer" }}
            onClick={() => setVyjezdInvoiceFilter("neodeslano")}
            title="Zobrazit jen neodeslanou výjezdovou fakturaci"
          >
            <div style={{ fontSize: 12, color: "#4b5563", fontWeight: 800 }}>Výjezd neodesláno</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#4b5563", marginTop: 6 }}>{stats.neodeslanoVyjezd}</div>
          </div>

          <div
            style={{ ...statCardStyle, cursor: "pointer" }}
            onClick={() => setVyjezdInvoiceFilter("odeslano")}
            title="Zobrazit jen odeslanou výjezdovou fakturaci"
          >
            <div style={{ fontSize: 12, color: "#1d4ed8", fontWeight: 800 }}>Výjezd odesláno</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#1d4ed8", marginTop: 6 }}>{stats.odeslanoVyjezd}</div>
          </div>

          <div
            style={{ ...statCardStyle, cursor: "pointer" }}
            onClick={() => setVyjezdInvoiceFilter("po_splatnosti")}
            title="Zobrazit jen výjezdovou fakturaci po splatnosti"
          >
            <div style={{ fontSize: 12, color: "#991b1b", fontWeight: 800 }}>Výjezd po splatnosti</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#991b1b", marginTop: 6 }}>{stats.poSplatnostiVyjezd}</div>
          </div>

          <div
            style={{ ...statCardStyle, cursor: "pointer" }}
            onClick={() => setVyjezdInvoiceFilter("uhrazeno")}
            title="Zobrazit jen uhrazenou výjezdovou fakturaci"
          >
            <div style={{ fontSize: 12, color: "#166534", fontWeight: 800 }}>Výjezd uhrazeno</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#166534", marginTop: 6 }}>{stats.uhrazenoVyjezd}</div>
          </div>
        <div style={statCardStyle}>
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>Celkem obrat</div>
          <div style={{ fontSize: 24, fontWeight: 900, color: "#111827", marginTop: 6 }}>{formatMoney(stats.suma)}</div>
        </div>
        <div style={statCardStyle}>
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>Uhrazeno</div>
          <div style={{ fontSize: 24, fontWeight: 900, color: "#166534", marginTop: 6 }}>{formatMoney(stats.uhrazenoCastka)}</div>
        </div>
        <div style={statCardStyle}>
          <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>Zbývá</div>
          <div style={{ fontSize: 24, fontWeight: 900, color: "#991b1b", marginTop: 6 }}>{formatMoney(stats.zbyva)}</div>
        </div>
      </div>

      <div style={toolbarStyle}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(String(e.target.value || ""))}
          placeholder="Hledat podle jména, objednavatele, místa, ID, poznámky..."
          style={inputStyle}
        />

        <select value={typeFilter} onChange={(e) => setTypeFilter(String(e.target.value || ""))} style={inputStyle}>
          <option value="">Všechny typy</option>
          <option value="kremace">Kremace</option>
          <option value="kremace_bez_obradu">Kremace bez obřadu</option>
          <option value="pohreb">Pohřeb</option>
          <option value="obrad_s_urnou">Obřad s urnou</option>
          <option value="alternativa">Alternativa</option>
          <option value="prevoz">Převoz</option>
          <option value="ostatni">Ostatní</option>
        </select>

        <select value={financeFilter} onChange={(e) => setFinanceFilter(String(e.target.value || ""))} style={inputStyle}>
          <option value="">Všechny finance</option>
          <option value="uhrazeno">Uhrazeno</option>
          <option value="castecne">Částečně uhrazeno</option>
          <option value="zalohovano">Jen záloha</option>
          <option value="neuhrazeno">Neuhrazeno</option>
          <option value="bez_castek">Bez částek</option>
        </select>

        
        <select value={statusFilter} onChange={(e) => setStatusFilter(String(e.target.value || ""))} style={inputStyle}>
          <option value="">Všechny stavy</option>
          <option value="rozpracovano">Rozpracováno</option>
          <option value="cek_obrad">Čeká na obřad</option>
          <option value="doplatek">Čeká na doplatek</option>
          <option value="uhrazeno">Uhrazeno</option>
          <option value="hotovo">Hotovo</option>
          <option value="storno">Storno</option>
        </select>

        <select value={familyFilter} onChange={(e) => setFamilyFilter(String(e.target.value || ""))} style={inputStyle}>
          <option value="">Rodinná část: všechny</option>
          <option value="navazuje_rodina">Navazuje rodinná část</option>
          <option value="jen_vyjezd">Jen výjezd</option>
        </select>
          <select value={vyjezdInvoiceFilter} onChange={(e) => setVyjezdInvoiceFilter(String(e.target.value || ""))} style={inputStyle}>
            <option value="">Výjezd fakturace: vše</option>
            <option value="neodeslano">Neodesláno</option>
            <option value="odeslano">Odesláno</option>
            <option value="po_splatnosti">Po splatnosti</option>
            <option value="uhrazeno">Uhrazeno</option>
          </select>


        <select value={sortBy} onChange={(e) => setSortBy(String(e.target.value || ""))} style={inputStyle}>
          <option value="date_desc">Nejnovější datum</option>
          <option value="date_asc">Nejstarší datum</option>
          <option value="name_asc">Jméno A–Z</option>
          <option value="name_desc">Jméno Z–A</option>
          <option value="finance_desc">Nejvyšší částka</option>
        </select>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            style={actionBtnStyle}
            onClick={() => {
              setStatusFilter("cek_fakturace");
            }}
          >
            Jen k fakturaci
          </button>

          <button
          type="button"
          style={actionBtnStyle}
          onClick={() => {
            setSearch("");
            setTypeFilter("");
            setFinanceFilter("");
              setStatusFilter("");
              setFamilyFilter("");
            setSortBy("date_desc");
          }}
        >
          Vyčistit filtry
        </button>
        </div>

        <div style={{ textAlign: "right", fontWeight: 800, color: "#475569" }}>
          {filteredRows.length} záznamů
        </div>
      </div>

      <div style={splitStyle}>
        <div style={listWrapStyle}>
          <div
            style={{
              padding: 14,
              borderBottom: "1px solid #e5e7eb",
              fontSize: 16,
              fontWeight: 900,
              color: "#111827",
              background: "#f8fafc",
            }}
          >
            Přehled zakázek ({filteredRows.length})
          </div>

          <div style={{ maxHeight: 900, overflow: "auto" }}>
            {filteredRows.length ? filteredRows.map((row) => {
              const active = selectedRow?.id === row.id;
              return (
                <div
                  key={row.id}
                  onClick={() => setSelectedId(row.id)}
                  style={{
                    ...rowCardStyle,
                    background: active ? "#f8fbff" : "#fff",
                    borderLeft: active ? "4px solid #2563eb" : "4px solid transparent",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontSize: 18, fontWeight: 900, color: "#111827" }}>
                          {valueOrDash(row.zesnuly)}
                        </div>
                        <div style={{ marginTop: 4, color: "#475569", fontWeight: 700 }}>
                          {row.typLabel} • {valueOrDash(row.id)}
                        </div>
                        <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <div style={orderStatusBadge(row.orderStatus)}>
                            {row.orderStatus?.label || "Rozpracováno"}
                          </div>
                          <div style={familyStatusBadge(row.familyStatus)}>
                            {row.familyStatus?.label || "Jen výjezd"}
                          </div>
                            <div style={vyjezdInvoiceBadge(row.vyjezdInvoiceStatus)}>
                              {row.vyjezdInvoiceStatus?.label || "Neodesláno"}
                            </div>
                          <div style={financeBadge(row.finance.state)}>
                            {financeLabel(row.finance.state)}
                          </div>
                        </div>
                      </div>
                    </div>

                  <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <div style={{ color: "#374151", fontWeight: 700 }}>
                      Datum: <span style={{ color: "#111827" }}>{valueOrDash(formatDate(row.hlavniDatum))}</span>
                    </div>
                    <div style={{ color: "#374151", fontWeight: 700 }}>
                      Čas: <span style={{ color: "#111827" }}>{valueOrDash(row.hlavniCas)}</span>
                    </div>
                    <div style={{ color: "#374151", fontWeight: 700 }}>
                      Objednavatel: <span style={{ color: "#111827" }}>{valueOrDash(row.objednavatel)}</span>
                    </div>
                    <div style={{ color: "#374151", fontWeight: 700 }}>
                      Celkem: <span style={{ color: "#111827" }}>{formatMoney(row.finance.total)}</span>
                    </div>
                  </div>

                  <div style={{ marginTop: 8, color: "#6b7280", fontSize: 13 }}>
                    {valueOrDash(row.misto)}
                  </div>
                </div>
              );
            }) : (
              <div style={{ padding: 18, color: "#6b7280", fontWeight: 700 }}>
                Pro aktuální filtr nebyly nalezeny žádné zakázky.
              </div>
            )}
          </div>
        </div>

        <div style={detailWrapStyle}>
          {selectedRow ? (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: 30, fontWeight: 900, color: "#111827" }}>
                    {valueOrDash(selectedRow.zesnuly)}
                  </div>
                  <div style={{ marginTop: 6, color: "#475569", fontWeight: 800 }}>
                    {selectedRow.typLabel} • ID {valueOrDash(selectedRow.id)}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button type="button" style={actionBtnStyle} onClick={printDetail}>
                    Tisk detailu
                  </button>
                  <button type="button" style={actionBtnStyle} onClick={exportDetailJson}>
                    Export JSON
                  </button>
                  <button type="button" style={actionBtnStyle} onClick={() => onOpenOrder?.(selectedRow.id, "zaklad")}>
                    Otevřít zakázku
                  </button>
                </div>
              </div>

              <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button type="button" style={actionBtnStyle} onClick={() => onOpenOrder?.(selectedRow.id, "zaklad")}>Základ</button>
                <button type="button" style={actionBtnStyle} onClick={() => onOpenOrder?.(selectedRow.id, "obrad")}>Obřad</button>
                <button type="button" style={actionBtnStyle} onClick={() => onOpenOrder?.(selectedRow.id, "prevoz")}>Převoz</button>
                <button type="button" style={actionBtnStyle} onClick={() => onOpenOrder?.(selectedRow.id, "platba")}>Platba</button>
                <button type="button" style={actionBtnStyle} onClick={() => onOpenOrder?.(selectedRow.id, "final")}>Finální objednávka</button>
              </div>

              
                <div
                  style={{
                    marginTop: 12,
                    display: "grid",
                    gridTemplateColumns: "minmax(240px, 320px) auto",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  <select
                    value={String(selectedRow?.zaklad?.stavZakazky || "")}
                    onChange={(e) => saveManualStatus(String(e.target.value || ""))}
                    style={inputStyle}
                    disabled={statusSaving}
                  >
                    <option value="">Nechat automatický stav</option>
                    <option value="rozpracovano">Rozpracováno</option>
                    <option value="cek_obrad">Čeká na obřad</option>
                    <option value="doplatek">Čeká na doplatek</option>
                    <option value="uhrazeno">Uhrazeno</option>
                    <option value="hotovo">Hotovo</option>
                    <option value="storno">Storno</option>
                  </select>

                  <div style={{ fontWeight: 800, color: "#475569" }}>
                    {statusSaving ? "Ukládám stav zakázky..." : "Ruční provozní stav zakázky"}
                  </div>
                </div>

<div style={{ display: "grid", gap: 14, marginTop: 18 }}>
                {detailBlock("Hlavní přehled", infoGrid([
                  { label: "ID zakázky", value: selectedRow.id },
                  { label: "Typ", value: selectedRow.typLabel },
                  { label: "Provozní stav", value: selectedRow.orderStatus?.label || "Rozpracováno" },
                  { label: "Rodinná část", value: selectedRow.familyStatus?.label || "Jen výjezd" },
                    { label: "Výjezd fakturace", value: selectedRow.vyjezdInvoiceStatus?.label || "Neodesláno" },
                  { label: "Datum", value: formatDate(selectedRow.hlavniDatum) },
                  { label: "Čas", value: selectedRow.hlavniCas },
                  { label: "Místo", value: selectedRow.misto },
                  { label: "Poznámka", value: selectedRow.note },
                ]))}

                {detailBlock("Zesnulý", infoGrid([
                  { label: "Jméno", value: [selectedRow.zesnula?.jmeno, selectedRow.zesnula?.prijmeni].filter(Boolean).join(" ") || selectedRow.zesnuly },
                  { label: "Rodné číslo", value: selectedRow.zesnula?.rodneCislo || selectedRow.vstupni?.rodneCislo },
                  { label: "Datum narození", value: formatDate(selectedRow.zesnula?.datumNarozeni || selectedRow.vstupni?.datumNarozeni) },
                  { label: "Datum úmrtí", value: formatDate(selectedRow.zesnula?.datumUmrti || selectedRow.vstupni?.datumUmrti) },
                  { label: "Čas úmrtí", value: selectedRow.zesnula?.casUmrti || selectedRow.vstupni?.casUmrti },
                  { label: "Místo úmrtí", value: selectedRow.zesnula?.mistoUmrti || selectedRow.vstupni?.mistoUmrti },
                  { label: "Adresa úmrtí", value: selectedRow.zesnula?.adresaUmrti || selectedRow.vstupni?.adresaUmrti },
                  { label: "Trvalé bydliště", value: selectedRow.zesnula?.trvaleBydliste },
                ]))}

                {detailBlock("Objednavatel / vypravitel", infoGrid([
                  { label: "Jméno", value: [selectedRow.vypravitel?.jmeno, selectedRow.vypravitel?.prijmeni].filter(Boolean).join(" ") || selectedRow.objednavatel },
                  { label: "Telefon", value: selectedRow.vypravitel?.telefon },
                  { label: "E-mail", value: selectedRow.vypravitel?.email },
                  { label: "Vztah", value: selectedRow.vypravitel?.vztah },
                  { label: "Adresa", value: selectedRow.vypravitel?.adresa },
                  { label: "Město", value: selectedRow.vypravitel?.mesto },
                  { label: "PSČ", value: selectedRow.vypravitel?.psc },
                ]))}

                {detailBlock("Obřad / převoz", infoGrid([
                  { label: "Obřad typ", value: selectedRow.obrad?.typ },
                  { label: "Obřad místo", value: selectedRow.obrad?.misto },
                  { label: "Obřad adresa", value: selectedRow.obrad?.mistoAdresa },
                  { label: "Krematorium", value: selectedRow.obrad?.krematorium },
                  { label: "Krematorium adresa", value: selectedRow.obrad?.krematoriumAdresa },
                  { label: "Hřbitov", value: selectedRow.obrad?.hrbitov },
                  { label: "Převoz místo odvozu", value: selectedRow.prevoz?.mistoOdvozu },
                  { label: "Převoz adresa", value: selectedRow.prevoz?.adresaOdvozu },
                  { label: "Cíl převozu", value: selectedRow.prevoz?.cilPrevozuNazev },
                  { label: "KM", value: selectedRow.prevoz?.km },
                  { label: "Vozidlo", value: selectedRow.prevoz?.vozidlo },
                ]))}

                {detailBlock("Finance / účetnictví", (
                  <div style={{ display: "grid", gap: 10 }}>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <div style={orderStatusBadge(selectedRow.orderStatus)}>
                          {selectedRow.orderStatus?.label || "Rozpracováno"}
                        </div>
                          <div style={familyStatusBadge(selectedRow.familyStatus)}>
                            {selectedRow.familyStatus?.label || "Jen výjezd"}
                          </div>
                            <div style={vyjezdInvoiceBadge(selectedRow.vyjezdInvoiceStatus)}>
                              {selectedRow.vyjezdInvoiceStatus?.label || "Neodesláno"}
                            </div>
                        <div style={financeBadge(selectedRow.finance.state)}>
                          {financeLabel(selectedRow.finance.state)}
                        </div>
                      </div>

                    {infoGrid([
                      { label: "Služby celkem", value: formatMoney(selectedRow.finance.servicesTotal) },
                      { label: "Květiny celkem", value: formatMoney(selectedRow.finance.flowersTotal) },
                      { label: "Zakázka celkem", value: formatMoney(selectedRow.finance.total) },
                      { label: "Zálohová platba", value: formatMoney(selectedRow.finance.zalohy) },
                      { label: "Uhrazeno celkem", value: formatMoney(selectedRow.finance.uhrazeno) },
                      { label: "Zbývá doplatit", value: formatMoney(selectedRow.finance.outstanding) },
                      { label: "Způsob úhrady", value: selectedRow.platba?.zpusobUhrady },
                      { label: "Datum úhrady", value: formatDate(selectedRow.platba?.datumUhrady) },
                      { label: "Poznámka k platbě", value: selectedRow.platba?.poznamkaPlatba },
                    ])}
                  </div>
                ))}

                {detailBlock("Řeč / řečnice", infoGrid([
                  { label: "Stav řeči", value: selectedRow.rec?.stav },
                  { label: "Kontakt rodina", value: selectedRow.rec?.kontaktRodinaJmeno },
                  { label: "E-mail rodiny", value: selectedRow.rec?.kontaktRodinaEmail },
                  { label: "Sdílení povoleno", value: selectedRow.rec?.sdileniPovoleno ? "Ano" : "Ne" },
                  { label: "Stav rodina", value: selectedRow.rec?.stavRodina },
                  { label: "Potvrzeno rodinou", value: selectedRow.rec?.potvrzenoRodinou ? "Ano" : "Ne" },
                ]))}

                {detailBlock("ABRA Gen / budoucí napojení", (
                  <div style={{ display: "grid", gap: 10 }}>
                    <div style={{ color: "#374151", fontWeight: 700 }}>
                      Tento blok je připravený pro budoucí napojení na účetnictví a fakturaci.
                    </div>

                    {infoGrid([
                      { label: "Interní ID zakázky", value: selectedRow.id },
                      { label: "Typ zakázky", value: selectedRow.typ },
                      { label: "Finance stav", value: financeLabel(selectedRow.finance.state) },
                      { label: "Celková částka", value: formatMoney(selectedRow.finance.total) },
                      { label: "Uhrazeno", value: formatMoney(selectedRow.finance.uhrazeno) },
                      { label: "Doplatek", value: formatMoney(selectedRow.finance.outstanding) },
                    ])}

                    <div
                      style={{
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        borderRadius: 12,
                        padding: 12,
                        color: "#475569",
                        fontSize: 13,
                        lineHeight: 1.5,
                      }}
                    >
                      Sem později doplníme: číslo dokladu ABRA, stav synchronizace, datum exportu,
                      variabilní symbol, účetní středisko, typ dokladu a log přenosu.
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ color: "#6b7280", fontWeight: 700 }}>
              Není vybraná žádná zakázka.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
